import { ChevronDown, ChevronRight } from 'lucide-react';

import type { Resumo } from './alvoGrouping';

interface HeaderProps {
  titulo: string;
  descricao?: string;
  resumo: Resumo;
  aberto: boolean;
  onToggle: () => void;
  /** Indentação à esquerda em `px` — usado para sub-grupos (ramos dentro de Tribunais). */
  indentPx?: number;
}

/**
 * Header colapsável para uma seção de alvos. Mostra contagem total e um
 * mini-resumo por status (não renderiza o “0” quando a contagem é zero
 * para evitar ruído visual). Clique no header inteiro alterna aberto/fechado.
 */
export function GrupoAlvosHeader({
  titulo,
  descricao,
  resumo,
  aberto,
  onToggle,
  indentPx = 0,
}: HeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={aberto}
      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted/50"
      style={{ paddingLeft: 8 + indentPx }}
    >
      {aberto ? (
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold">{titulo}</span>
          <span className="text-xs text-muted-foreground">({resumo.total})</span>
        </div>
        {descricao && <p className="text-xs text-muted-foreground">{descricao}</p>}
      </div>
      <ResumoPill resumo={resumo} />
    </button>
  );
}

function ResumoPill({ resumo }: { resumo: Resumo }) {
  if (resumo.total === 0) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      {resumo.ativo > 0 && (
        <span className="flex items-center gap-1 text-green-700 dark:text-green-400">
          <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
          {resumo.ativo}
        </span>
      )}
      {resumo.degradado > 0 && (
        <span className="flex items-center gap-1 text-yellow-700 dark:text-yellow-400">
          <span className="inline-block h-2 w-2 rounded-full bg-yellow-500" />
          {resumo.degradado}
        </span>
      )}
      {resumo.indisponivel > 0 && (
        <span className="flex items-center gap-1 text-red-700 dark:text-red-400">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
          {resumo.indisponivel}
        </span>
      )}
      {resumo.semDados > 0 && (
        <span
          className="flex items-center gap-1 text-gray-600 dark:text-gray-400"
          title="Alvos sem dados recentes (não confundir com indisponibilidade)"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
          {resumo.semDados}
        </span>
      )}
    </div>
  );
}
